/**
 * Format a date string or Date object to a readable format
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} format - Format type: 'short', 'long', 'datetime', 'time'
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, format = 'datetime') => {
  try {
    const date = new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // Month names
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const longMonthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Get date components
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Format time (12-hour format with AM/PM)
    const formattedTime = (() => {
      const hour12 = hours % 12 || 12;
      const ampm = hours < 12 ? 'AM' : 'PM';
      const minuteStr = minutes.toString().padStart(2, '0');
      return `${hour12}:${minuteStr} ${ampm}`;
    })();

    // Format based on requested format
    switch (format) {
      case 'short':
        // 22 Jan 2026
        return `${day} ${monthNames[month]} ${year}`;
        
      case 'long':
        // 22 January 2026
        return `${day} ${longMonthNames[month]} ${year}`;
        
      case 'time':
        // 11:30 PM
        return formattedTime;
        
      case 'datetime':
      default:
        // 22 Jan 2026, 11:30 PM
        return `${day} ${monthNames[month]} ${year}, ${formattedTime}`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  try {
    const date = new Date(dateInput);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffSec < 60) {
      return 'Just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else if (diffWeek < 4) {
      return `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
    } else if (diffMonth < 12) {
      return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Some time ago';
  }
};

/**
 * Check if a date is today
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {boolean} True if date is today
 */
export const isToday = (dateInput) => {
  try {
    const date = new Date(dateInput);
    const today = new Date();
    
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  } catch (error) {
    return false;
  }
};

/**
 * Format date with "Today" or "Yesterday" prefix if applicable
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateWithRelative = (dateInput) => {
  try {
    const date = new Date(dateInput);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (isToday(dateInput)) {
      return `Today, ${formatDate(dateInput, 'time')}`;
    } else if (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    ) {
      return `Yesterday, ${formatDate(dateInput, 'time')}`;
    } else {
      return formatDate(dateInput, 'datetime');
    }
  } catch (error) {
    return formatDate(dateInput, 'datetime');
  }
};

/**
 * Format date for display in table cells
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Object} Object with full and short date strings
 */
export const getDateForTable = (dateInput) => {
  return {
    full: formatDate(dateInput, 'datetime'),
    short: formatDate(dateInput, 'short'),
    relative: formatRelativeTime(dateInput),
    isToday: isToday(dateInput)
  };
};

// Example usage:
// formatDate('2026-01-22T23:30:00Z') // Returns: "22 Jan 2026, 11:30 PM"
// formatDate('2026-01-22T23:30:00Z', 'short') // Returns: "22 Jan 2026"
// formatDate('2026-01-22T23:30:00Z', 'long') // Returns: "22 January 2026"
// formatDate('2026-01-22T23:30:00Z', 'time') // Returns: "11:30 PM"
// formatRelativeTime('2026-01-22T23:30:00Z') // Returns relative time
// formatDateWithRelative('2026-01-22T23:30:00Z') // Returns: "Today, 11:30 PM" if today
// getDateForTable('2026-01-22T23:30:00Z') // Returns object with multiple formats